import type { ReactElement, ReactNode } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

/**
 * The pieces of a large form dialog, as in 新建销售/采购 and 新增商品: white section cards on a
 * light surface, labels above fields, one-line notes.
 */
export const SURFACE = '#f8fafc';
/** Fields on a grey card keep a white fill inside their own outline, corners included. */
export const ON_SURFACE = { '& .MuiInputBase-root': { bgcolor: 'background.paper' } } as const;

/** A white card with an icon and title; `extra` follows the title, `action` sits on the right. */
export function Section(props: {
  icon: ReactElement;
  title: string;
  required?: boolean;
  extra?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          minHeight: 32,
          pb: 1.5,
          mb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'primary.main',
            '& svg': { fontSize: 18 },
          }}
        >
          {props.icon}
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
            {props.title}
            {props.required && (
              <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                *
              </Box>
            )}
          </Typography>
          {props.extra}
        </Box>
        {/* Overlaps the header's padding, so a button there does not push the rule down and
            every card's title sits the same distance above it. */}
        {props.action && <Box sx={{ my: -1, flexShrink: 0 }}>{props.action}</Box>}
      </Box>
      {props.children}
    </Paper>
  );
}

/** A one-line explanation on a light panel, as under the counterparty. */
export function Note(props: { icon: ReactElement; children: ReactNode; sx?: SxProps<Theme> }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 1,
        fontSize: 12.5,
        color: 'text.secondary',
        bgcolor: SURFACE,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        '& svg': { fontSize: 16, color: 'primary.main', flexShrink: 0 },
        ...props.sx,
      }}
    >
      {props.icon}
      <span>{props.children}</span>
    </Box>
  );
}

/** A label above a field, as the design reads, instead of a floating one. */
export function FieldLabel(props: { children: ReactNode; required?: boolean; hint?: string }) {
  return (
    <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: 'text.secondary', mb: 0.75 }}>
      {props.children}
      {props.required && (
        <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
          *
        </Box>
      )}
      {props.hint && (
        <Box component="span" sx={{ color: 'text.disabled', ml: 0.5 }}>
          （{props.hint}）
        </Box>
      )}
    </Typography>
  );
}
